/**
 * Where Signalements is, from the URL hash, so the list and each report are
 * links that can be shared (and that the dashboard's alerts open):
 *
 * - `#reports` is every report; `#reports?severity=critical&status=open` filtered;
 * - `#reports?reviewOverdue=true`: only interim suspensions past their review deadline;
 * - `#reports/<safetyReportId>` opens one report beside the list, keeping the filters.
 *
 * The query names are the endpoint's own, so a dashboard link's filters open as they are.
 */
import {
  SAFETY_REPORT_SEVERITIES,
  SAFETY_REPORT_STATUSES,
  type SafetyReportSeverity,
  type SafetyReportStatus,
} from "@wallyo/contracts/console/safety";
import type { ListSafetyReportsQuery } from "../../api/console-api";

export type ReportFilters = {
  severity: SafetyReportSeverity | null;
  status: SafetyReportStatus | null;
  reviewOverdue: boolean;
};

export const ALL_REPORTS: ReportFilters = { severity: null, status: null, reviewOverdue: false };

export type ReportsRoute = { filters: ReportFilters; safetyReportId: string | null };

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | null =>
  value !== null && (values as readonly string[]).includes(value) ? (value as T) : null;

export function parseReportsHash(hash: string): ReportsRoute {
  const rest = hash.replace(/^#\/?reports/, "");
  const [path = "", search = ""] = rest.split("?", 2);
  const params = new URLSearchParams(search);
  let safetyReportId: string | null = null;
  try {
    safetyReportId = decodeURIComponent(path.replace(/^\/+/, "").split("/")[0] ?? "").trim() || null;
  } catch {
    safetyReportId = null;
  }
  return {
    filters: {
      severity: oneOf(SAFETY_REPORT_SEVERITIES, params.get("severity")),
      status: oneOf(SAFETY_REPORT_STATUSES, params.get("status")),
      reviewOverdue: params.get("reviewOverdue") === "true",
    },
    safetyReportId,
  };
}

/** The hash for the list with these filters, and a report open beside it. */
export function reportsHref(filters: ReportFilters, safetyReportId: string | null = null): string {
  const params = new URLSearchParams();
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.status) params.set("status", filters.status);
  if (filters.reviewOverdue) params.set("reviewOverdue", "true");
  const query = params.toString();
  const base = safetyReportId ? `#reports/${encodeURIComponent(safetyReportId)}` : "#reports";
  return query ? `${base}?${query}` : base;
}

/** What the list asks the server for. */
export function reportsRequest(filters: ReportFilters): ListSafetyReportsQuery {
  const request: ListSafetyReportsQuery = {};
  if (filters.severity) request.severity = filters.severity;
  if (filters.status) request.status = filters.status;
  if (filters.reviewOverdue) request.reviewOverdue = true;
  return request;
}
